import { Component, Input, OnInit } from '@angular/core';
import { LabOrder } from '../../shared/data/lab-order';
import { LabOrderBusinessService } from '../../shared/lab-order-business.service';
import { Coating } from '../../shared/data/coating';
import { LensCatalogBusinessService } from '../../shared/lens-catalog-business.service';
import { LensStyleApiService } from '../../shared/lens-style.api.service';
import { UtilsService } from '../../shared/utils.service';

export const COATING_NONE = ' |';
export const GLASS_COATING_NONE = ' ';
const AR_COATING_OTHER = 'QM|Other (AR Coating A)';
const MIRROR_COATING_OTHER = 'QR|Other';
// according to lens-catalog-api/arcoatings
const AR_COATING_OTHER_LABSERVICE_ID = 'A24';


export interface CoatingsDisplayGroup {
  group: string;
  items: Coating[];
}

@Component({
  selector: 'app-coating',
  templateUrl: './coating.component.html',
  styleUrls: ['./coating.component.css']
})
export class CoatingComponent implements OnInit {

  @Input() labOrder: LabOrder;

  readonly PRESS_ON_PRISM_VALUE = 'SH';
  readonly NOSE_PADS_VALUE = 'SS';
  readonly SLAB_OFF_VALUE = 'ST';

  // Arrays of Coating objects to populate the drop down lists' options
  arCoatings: Coating[];
  arCoatingsDisplay: CoatingsDisplayGroup[];
  scratchCoatings: Coating[];
  glassCoatings: Coating[];
  mirrorSkiCoatings: Coating[];
  mirrorSkiCoatingsDisplay: CoatingsDisplayGroup[];
  uvCoatings: Coating[];
  miscellaneous: Map<string, Coating>;

  // Maps of coating objects to populate all coating fields on update
  mirrorSkiCoatingsMap: Map<string, Coating>;
  uvCoatingsMap: Map<string, Coating>;
  glassCoatingsMap: Map<string, Coating>;
  scratchCoatingsMap: Map<string, Coating>;
  arCoatingsMap: Map<string, Coating>;

  // local variables to indirectly map functions to disabled fields.
  mirrorSkiDisabled = false;
  antiReflectiveDisabled = false;
  glassCoatingDisabled = false;

  constructor(
    private labOrderBusinessService: LabOrderBusinessService,
    private lensCatalogBusinessService: LensCatalogBusinessService
  ) { }

  ngOnInit(): void  {
    this.arCoatings = this.lensCatalogBusinessService.arCoatings;
    this.arCoatingsDisplay = this.lensCatalogBusinessService.arCoatingsDisplay;
    this.scratchCoatings = UtilsService.sortObjectArray(this.lensCatalogBusinessService.scratchCoatings, 'description');
    this.uvCoatings = this.lensCatalogBusinessService.uvCoatings;
    this.mirrorSkiCoatings = this.lensCatalogBusinessService.mirrorSkiCoatings;
    this.mirrorSkiCoatingsDisplay = this.lensCatalogBusinessService.mirrorSkiCoatingsDisplay;
    this.glassCoatings = this.lensCatalogBusinessService.glassCoatings;
    this.miscellaneous = this.lensCatalogBusinessService.miscellaneous;

    this.mirrorSkiCoatingsMap = this.lensCatalogBusinessService.mirrorSkiCoatingsMap;
    this.uvCoatingsMap = this.lensCatalogBusinessService.uvCoatingsMap;
    this.glassCoatingsMap = this.lensCatalogBusinessService.glassCoatingsMap;
    this.scratchCoatingsMap = this.lensCatalogBusinessService.scratchCoatingsMap;
    this.arCoatingsMap = this.lensCatalogBusinessService.arCoatingsMap;

    // retrieve the mirrorSkiCoatings from Lens Catalog API after loaded
    if (this.labOrder.lens.mirrorSkiCoating) {

      // when no code found, set to 'None'
      if (!this.labOrder.lens.mirrorSkiCoating.codes) {
        this.labOrder.lens.mirrorSkiCoating.hashValue = COATING_NONE;
      } else {
        // when code = 'QP' or 'QR' and description is not empty, but not found in mirrorSkiCoatingMap then set to 'Other'
        const preferredCode = this.getPreferredCode(this.labOrder.lens.mirrorSkiCoating.codes);
        const hashValue = this.getHashValue(preferredCode, this.labOrder.lens.mirrorSkiCoating.description);

        // if not found in mirrorSkiCoatingMap then set to 'Other'
        if (! this.mirrorSkiCoatingsMap.has(hashValue)) {
          this.labOrder.lens.mirrorSkiCoating.hashValue = MIRROR_COATING_OTHER;
          // no current labServiceId for mirror/Sky coating of 'other'
          this.labOrder.lens.mirrorSkiCoating.labServiceId = null;
        }else {
          const coating: Coating = this.mirrorSkiCoatingsMap.get(hashValue);
          if (coating) {
            this.labOrder.lens.mirrorSkiCoating =
              new Coating(coating.codes, coating.description, coating.labServiceId, coating.coatingGroup);
          }
        }
      }
    }

    // has the coatings objects
    if (this.labOrder.lens.antiReflectiveCoating) {

      // when no code found, set to 'None'
      if (!this.labOrder.lens.antiReflectiveCoating.codes) {
        this.labOrder.lens.antiReflectiveCoating.hashValue = COATING_NONE;
      } else {

        // special case for Legacy orders that have coating description case-differences.
        if (this.labOrder.lens.antiReflectiveCoating.description &&
          this.labOrder.lens.antiReflectiveCoating.description.startsWith('KODAK')) {
          this.labOrder.lens.antiReflectiveCoating.description =
            this.labOrder.lens.antiReflectiveCoating.description.replace('KODAK', 'Kodak');
        }

        // when code = 'QM' and description is not empty, but not found in arCoatingMap then set to 'Other'
        const preferredCode = this.getPreferredCode(this.labOrder.lens.antiReflectiveCoating.codes);
        const hashValue =
          this.getHashValue(preferredCode, this.labOrder.lens.antiReflectiveCoating.description);

        // if not found in arCoatingsMap then set to 'Other'
        if (! this.arCoatingsMap.has(hashValue)) {
          this.labOrder.lens.antiReflectiveCoating.hashValue = AR_COATING_OTHER;
          this.labOrder.lens.antiReflectiveCoating.labServiceId = AR_COATING_OTHER_LABSERVICE_ID;
        } else {
          const coating: Coating = this.arCoatingsMap.get(hashValue);
          if (coating) {
            this.labOrder.lens.antiReflectiveCoating =
              new Coating(coating.codes, coating.description, coating.labServiceId, coating.coatingGroup);
          }
        }
      }
    }

    if (this.labOrder.lens.uvCoating) {
      // when no code found, set to 'None'
      if (!this.labOrder.lens.uvCoating.codes) {
        this.labOrder.lens.uvCoating.hashValue = COATING_NONE;
      } else {
        const preferredCode = this.getPreferredCode(this.labOrder.lens.uvCoating.codes);
        const uvDescription: string =
          this.updateUvCoatingDescription(preferredCode, this.labOrder.lens.uvCoating.description);
        const hashValue: string = this.getHashValue(preferredCode, uvDescription);
        const coating: Coating = this.uvCoatingsMap.get(hashValue);
        if (coating) {
          this.labOrder.lens.uvCoating = new Coating(coating.codes, coating.description, coating.labServiceId);
        }
      }
    }

    if (this.labOrder.lens.glassCoatingOrTintType) {
      // when no code found, set to 'None'
      if (!this.labOrder.lens.glassCoatingOrTintType.codes) {
        this.labOrder.lens.glassCoatingOrTintType.hashValue = COATING_NONE;
      } else {
        const preferredCode = this.getPreferredCode(this.labOrder.lens.glassCoatingOrTintType.codes);
        const hashValue: string = this.getHashValue(preferredCode, this.labOrder.lens.glassCoatingOrTintType.description);
        const coating: Coating = this.glassCoatingsMap.get(hashValue);
        if (coating) {
          this.labOrder.lens.glassCoatingOrTintType = new Coating(coating.codes, coating.description, coating.labServiceId);
        }
      }
    }

    if (this.labOrder.lens.scratchCoating) {
      // when no code found, set to 'None'
      if (!this.labOrder.lens.scratchCoating.codes) {
        this.labOrder.lens.scratchCoating.hashValue = COATING_NONE;
      } else {
        // special case for Legacy orders that have coating descriptions stripped off at 15 chars.
        if (this.labOrder.lens.scratchCoating.description && this.labOrder.lens.scratchCoating.description === 'Hoya Clarity Sh') {
          this.labOrder.lens.scratchCoating.description = 'Hoya Clarity Shield';
        }

        const preferredCode = this.getPreferredCode(this.labOrder.lens.scratchCoating.codes);
        const hashValue: string = this.getHashValue(preferredCode, this.labOrder.lens.scratchCoating.description);
        const coating: Coating = this.scratchCoatingsMap.get(hashValue);
        if (coating) {
          this.labOrder.lens.scratchCoating = new Coating(coating.codes, coating.description, coating.labServiceId);
        }
      }
    }

    if (this.labOrder.lens.pressOnPrism && this.labOrder.lens.pressOnPrism.codes) {
      const coating: Coating = this.miscellaneous.get(this.getPreferredCode(this.labOrder.lens.pressOnPrism.codes));
      if (coating) {
        this.labOrder.lens.pressOnPrism.labServiceId = coating.labServiceId;
      }
    }
    if (this.labOrder.lens.slabOff && this.labOrder.lens.slabOff.codes) {
      const coating: Coating = this.miscellaneous.get(this.getPreferredCode(this.labOrder.lens.slabOff.codes));
      if (coating) {
        this.labOrder.lens.slabOff.labServiceId = coating.labServiceId;
      }
    }

    // have components work off a local variable rather than a direct function.
    // prevents calling function for every model change.
    this.mirrorSkiDisabled = this.isMirrorCoatingDescDisabled();
    this.antiReflectiveDisabled = this.isARCoatingDescDisabled();
    this.glassCoatingDisabled = this.isGlassCoatingDescDisabled();
  }

  private getHashValue(code: string, description: string): string {
    const hashValue: string = code + '|' + description;
    return hashValue;
  }

  // When the AR Coatings are changed, find the coating option selected based on the hasValue and set a new Coating
  // object into the labOrder object.
  public arUpdated() {
    this.antiReflectiveDisabled = true;

    const coating: Coating = this.arCoatingsMap.get(this.labOrder.lens.antiReflectiveCoating.hashValue);
    // for "none" coating, null all fields.
    if (!coating || coating.hashValue === COATING_NONE) {
      this.labOrder.lens.antiReflectiveCoating = this.emptyCoating();
    } else if (coating.hashValue === AR_COATING_OTHER) {
      // for "other" coating, blank out the description.
      this.labOrder.lens.antiReflectiveCoating.labServiceId = coating.labServiceId;
      this.labOrder.lens.antiReflectiveCoating.codes = coating.codes;
      this.labOrder.lens.antiReflectiveCoating.description = '';

      // AR Coating description field enabled
      this.antiReflectiveDisabled = false;
    } else {
      this.labOrder.lens.antiReflectiveCoating =
        new Coating(coating.codes, coating.description, coating.labServiceId, coating.coatingGroup);
    }
  }

  // When the AR Coatings are changed, find the coating option selected based on the hasValue and set a new Coating
  // object into the labOrder object.
  public scratchUpdated() {

    const coating: Coating = this.scratchCoatingsMap.get(this.labOrder.lens.scratchCoating.hashValue);

    // if coating not found or 'None'
    if (!coating || coating.hashValue === COATING_NONE) {
      this.labOrder.lens.scratchCoating = this.emptyCoating();
    } else {
      this.labOrder.lens.scratchCoating = new Coating(coating.codes, coating.description, coating.labServiceId);
    }
  }

  // When the glass coating is changed, find the coating option selected based on the hasValue and set a new Coating
  // object into the labOrder object.
  public glassUpdated() {

    const coating: Coating = this.glassCoatingsMap.get(this.labOrder.lens.glassCoatingOrTintType.hashValue);

    // if coating not found or 'None'
    if (!coating || coating.hashValue === COATING_NONE) {
      this.labOrder.lens.glassCoatingOrTintType = this.emptyCoating();
    } else {
      this.labOrder.lens.glassCoatingOrTintType = new Coating(coating.codes, coating.description, coating.labServiceId);
    }
  }

  // When the Mirror/Ski Coatings are changed, find the coating option selected based on the hasValue and set a new Coating
  // object into the labOrder object.
  public mirrorUpdated() {
    this.mirrorSkiDisabled = true;
    const coating: Coating = this.mirrorSkiCoatingsMap.get(this.labOrder.lens.mirrorSkiCoating.hashValue);
    // if coating not found or 'None'
    if (!coating || coating.hashValue === COATING_NONE) {
      this.labOrder.lens.mirrorSkiCoating = this.emptyCoating();
    } else if (coating.hashValue === MIRROR_COATING_OTHER) {
      // for "other" coating, blank out the description.
      this.labOrder.lens.mirrorSkiCoating.labServiceId = coating.labServiceId;
      this.labOrder.lens.mirrorSkiCoating.codes = coating.codes;
      this.labOrder.lens.mirrorSkiCoating.description = '';
      this.mirrorSkiDisabled = false;
    } else {
      this.labOrder.lens.mirrorSkiCoating =
        new Coating(coating.codes, coating.description, coating.labServiceId, coating.coatingGroup);
    }
  }

  // When the UV Coatings are changed, find the coating option selected based on the hasValue and set a new Coating
  // object into the labOrder object.
  public uvUpdated() {
    const coating: Coating = this.uvCoatingsMap.get(this.labOrder.lens.uvCoating.hashValue);

    // if coating not found or 'None'
    if (!coating || coating.hashValue === COATING_NONE) {
      this.labOrder.lens.uvCoating = this.emptyCoating();
    } else {
      this.labOrder.lens.uvCoating = new Coating(coating.codes, coating.description, coating.labServiceId);
    }
  }

  private emptyCoating(): Coating {
    const coating: Coating = new Coating(null, null);
    coating.hashValue = COATING_NONE;
    return coating;
  }

  // The following functions are triggered by the "change" attribute on their associated description inputs.
  // If the description doesn't match the list value, switch the selection to "other".  Only applies to AR Coating.
  public checkArDescription() {
    const descriptionAsEntered = this.labOrder.lens.antiReflectiveCoating.description;
    let otherNotListed;

    let found = false;
    this.arCoatings.forEach((item) => {

      // Note for future reference: this retrieves the first array element whose name starts with "Other" -
      // If there are more than one, this will always pick the first one.
      if (!otherNotListed && 'Other' === item.description.substr(0, 5)) {
        // save this for later...
        otherNotListed = item;
      }
      if (descriptionAsEntered === item.description) {
        this.labOrder.lens.antiReflectiveCoating = new Coating(item.codes, item.description);
        found = true;
      }
    });

    if (!found && otherNotListed) {
      this.labOrder.lens.antiReflectiveCoating = new Coating(otherNotListed.codes, otherNotListed.description);
      this.labOrder.lens.antiReflectiveCoating.description = descriptionAsEntered;
    }
  }


  // The following functions are triggered by the "change" attribute on their associated description inputs.
  // If the description doesn't match the list value, switch the selection to "other".  Only applies to Mirror/Ski Coating.
  public checkMirrorDescription() {
    const descriptionAsEntered = this.labOrder.lens.mirrorSkiCoating.description;
    let otherNotListed;

    let found = false;
    this.mirrorSkiCoatings.forEach((item) => {

      // Note for future reference: this retrieves the first array element whose name starts with "Other" -
      // If there are more than one, this will always pick the first one.
      if (!otherNotListed && 'Other' === item.description.substr(0, 5)) {
        // save this for later...
        otherNotListed = item;
      }
      if (descriptionAsEntered === item.description) {
        this.labOrder.lens.mirrorSkiCoating = new Coating(item.codes, item.description);
        found = true;
      }
    });

    if (!found && otherNotListed) {
      this.labOrder.lens.mirrorSkiCoating = new Coating(otherNotListed.codes, otherNotListed.description);
      this.labOrder.lens.mirrorSkiCoating.description = descriptionAsEntered;
    }
  }

  public setPressOnPrismCheckboxValue(checkBox: HTMLInputElement) {
    if (checkBox.checked) {
      this.labOrder.lens.pressOnPrism.codes = [this.PRESS_ON_PRISM_VALUE];
      const coating: Coating = this.miscellaneous.get(this.PRESS_ON_PRISM_VALUE);
      if (coating) {
        this.labOrder.lens.pressOnPrism.labServiceId = coating.labServiceId;
      }
    } else {
      this.labOrder.lens.pressOnPrism = this.emptyCoating();
    }
  }

  public setNosePadCheckboxValue(checkBox: HTMLInputElement) {
    if (checkBox.checked) {
      this.labOrder.lens.nosePads.codes = [this.NOSE_PADS_VALUE];
    } else {
      this.labOrder.lens.nosePads = this.emptyCoating();
    }
  }

  public setNosePadFromDescription(description: HTMLInputElement) {
    if (description.value && description.value.trim().length > 0) {
      this.labOrder.lens.nosePads = new Coating([this.NOSE_PADS_VALUE], description.value.trim());
    }
  }

  public setSlabOffCheckboxValue(checkBox: HTMLInputElement) {
    if (checkBox.checked) {
      this.labOrder.lens.slabOff.codes = [this.SLAB_OFF_VALUE];
      const coating: Coating = this.miscellaneous.get(this.SLAB_OFF_VALUE);
      if (coating) {
        this.labOrder.lens.slabOff.labServiceId = coating.labServiceId;
      }
    } else {
      this.labOrder.lens.slabOff = this.emptyCoating();
    }
  }

  isARCoatingDescDisabled() {
    if (this.labOrder.lens.antiReflectiveCoating && this.labOrder.lens.antiReflectiveCoating.hashValue === AR_COATING_OTHER) {
      return false;
    } else {
      return true;
    }
  }

  isMirrorCoatingDescDisabled() {
    if (this.labOrder.lens.mirrorSkiCoating && this.labOrder.lens.mirrorSkiCoating.hashValue === MIRROR_COATING_OTHER) {
      return false;
    } else {
      return true;
    }
  }

  isGlassCoatingDescDisabled() {

    //  NOTE: prior to the Options Reformulations 2023 project (see Jira labportal-547), the glass coating description field
    //  on the modify order screen was always getting set to "disabled" by this code.  Nothing would enable it.  When the
    //  modifications were made for OR2023, and labportal-547, the coating description field was now getting enabled as
    //  a side effect of those modifications.  This will allow a lab to modify the description of the glass coating when
    //  it was not being allowed before OR2023. A new Jira, labportal-554 was created to research this issue and determine
    //  how to proceed.  Thus, I am commenting out the following block of code in order to maintain the current production
    //  behavior of this field until that research is done and a decision is made to how to proceed.

    // let returnValue = false;

    // if (this.labOrder.lens.glassCoatingOrTintType) {
    //   const preferredCode = this.getPreferredCode(this.labOrder.lens.glassCoatingOrTintType.codes);
    //   if (preferredCode === ' ') {
    //     returnValue = true;
    //   }
    // }

    return true;
  }

  // because the Legacy application does not update the UV description
  // need to update to match what lens-catalog API is returning.
  private updateUvCoatingDescription(code: string, description: string): string {
    if (!code || code === ' ') {
      description = 'None';
    } else if (code === 'SV') {
      description = 'UV Coating';
    } else if (code === 'IV') {
      description = 'Dr Supplied UV Coating';
    }
    return description;
  }

  private getPreferredCode(codes: string[]): string {
    let returnCode: string = null;
    if (codes) {
      for (let index = 0; index < codes.length; index++) {
        if (!Coating.SECONDARY_CODES.includes(codes[index])) {
          returnCode = codes[index];
          break;
        }
      }
    }
    return returnCode;
  }
}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-sandbox-build/src/app/modify-order/coating/coating.component.ts